/* ==========================================================================
   core dna Documentation Hub — front-end behaviour (webpack entry)
   --------------------------------------------------------------------------
   Split from the former single-IIFE assets/hub/hub.js into per-feature ES
   modules sharing ./util.js. Bundled by webpack to dist/app.js; the SCSS
   import below is extracted to dist/app.css by MiniCssExtractPlugin.

   Vanilla, no framework. The one library, Prism (syntax highlighting), comes
   from npm and is bundled in, because the site's CSP (script-src 'self' …)
   blocks third-party bundles and app.js is same-origin.

   Every module feature-detects its own markup, so the one bundle serves the
   homepage, the landings and the Documentation-module article pages.
   ========================================================================== */
import '../scss/style.scss';

import { doc } from './util.js';
import { initTheme } from './theme.js';
import { initNavMenu, initMegaMenu } from './nav-menu.js';
import { initToc } from './toc.js';
import { initSidebar } from './sidebar.js';
import { initCodeCopy } from './code-copy.js';
import { initFaq } from './faq.js';
import { initFeedback } from './feedback.js';
import { initDisclosures } from './disclosures.js';
import { initDocsImageFallback } from './docs-image-fallback.js';
import { initHubForms } from './forms.js';
import { initStripEmptyForm } from './strip-empty-form.js';
import { initHomeProgress } from './home-progress.js';
import { initReference } from './reference.js';
import { initTreeFilter } from './tree-filter.js';
import { initCodeHighlight } from './code-highlight.js';
import { initTemplateCode } from './template-code.js';
import { initApiExample } from './api-example.js';
import { initApiPage } from './api-page.js';
import { initTableWrap } from './table-wrap.js';

/* Start one feature, and never let it take the others with it.

   The features are independent -- each feature-detects its own markup and knows
   nothing about the rest -- but they used to be started in one unprotected
   sequence, so the FIRST one to throw silently killed every feature after it.
   That is not hypothetical: initSidebar called window.matchMedia unguarded, so
   an environment without that API lost code-copy, the FAQ, feedback,
   disclosures, the image fallback, the forms and the live module reference,
   none of which have anything to do with a sidebar. The bad call is fixed, but
   the sequencing is the reason one bug became eight, so it is fixed too.

   Failures are logged rather than swallowed: silent is how the original
   problem stayed invisible. */
function start(name, init) {
	try {
		init();
	} catch (error) {
		if (window.console && window.console.error) {
			window.console.error('hub: ' + name + ' failed to start', error);
		}
	}
}

function boot() {
	start('theme', initTheme);
	start('table-wrap', initTableWrap);
	start('nav-menu', initNavMenu);
	start('mega-menu', initMegaMenu);
	start('toc', initToc);
	start('sidebar', initSidebar);
	start('code-copy', initCodeCopy);
	start('faq', initFaq);
	start('feedback', initFeedback);
	start('disclosures', initDisclosures);
	start('docs-image-fallback', initDocsImageFallback);
	start('forms', initHubForms);
	start('strip-empty-form', initStripEmptyForm);
	start('home-progress', initHomeProgress);
	start('reference', initReference);
	start('tree-filter', initTreeFilter);
	start('code-highlight', initCodeHighlight);
	start('template-code', initTemplateCode);
	start('api-example', initApiExample);
	start('api-page', initApiPage);
}

if (doc.readyState === 'loading') {
	doc.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
